import { useCallback, useEffect, useState } from "react";
import { Card, Empty, ErrorBanner, Pill, Tile, fmt } from "../components/ui";
import {
  api,
  type RedialBlock,
  type RedialPlan,
  type RedialPolicy,
  type RedialRule,
  type RedialStatus,
} from "../lib/api";

/** The switches, as controls rather than a read-out.
 *
 *  These were environment variables until 2026-09-30, so turning the engine on or off meant a
 *  `gcloud run services update` - a deploy - which is the wrong shape for something you want
 *  to do quickly when a cadence is misbehaving.
 *
 *  Starting real calls is the one action here that cannot be undone by clicking again, so it is
 *  the one that asks you to type LIVE. Everything else is a single click, deliberately: getting
 *  QUIETER should never be gated behind a form. */
function Controls({ s, onDone }: { s: RedialStatus; onDone: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [cap, setCap] = useState(String(s.daily_cap));
  const dialing = s.engine_enabled && !s.dry_run && s.five9_dialer_enabled;

  const send = (
    body: Parameters<typeof api.redialSetSettings>[0],
    what: string,
  ) => {
    setBusy(what);
    setErr(null);
    api
      .redialSetSettings(body)
      .then(onDone)
      .catch((e) => setErr(e.message))
      .finally(() => setBusy(null));
  };

  const goLive = () => {
    const typed = window.prompt(
      "This starts calling real people.\n\nType LIVE to confirm.",
    );
    if (typed == null) return;
    send(
      { enabled: true, dry_run: false, dialer_enabled: true, confirm: typed.trim() },
      "live",
    );
  };

  return (
    <Card title="Controls">
      <ErrorBanner error={err} />
      <div className="col" style={{ gap: 10 }}>
        {dialing ? (
          <button className="btn bad" disabled={busy !== null} onClick={() =>
            // Back to dry in ONE click. The fastest path out is not behind a confirmation:
            // somebody reaching for this is already sure.
            send({ dry_run: true, dialer_enabled: false }, "dry")
          }>
            {busy === "dry" ? "stopping…" : "Stop calling (back to dry run)"}
          </button>
        ) : (
          <button className="btn" disabled={busy !== null} onClick={goLive}>
            {busy === "live" ? "starting…" : "Start calling for real…"}
          </button>
        )}

        <button className="btn" disabled={busy !== null} onClick={() =>
          send({ enabled: !s.engine_enabled }, "engine")
        }>
          {busy === "engine"
            ? "…"
            : s.engine_enabled
              ? "Pause the engine entirely"
              : "Resume the engine"}
        </button>

        <form
          className="row"
          style={{ gap: 6, alignItems: "center" }}
          onSubmit={(e) => {
            e.preventDefault();
            const n = Number(cap);
            if (Number.isFinite(n) && n >= 1) send({ daily_cap: n }, "cap");
          }}
        >
          <label htmlFor="cap" className="mut">daily cap</label>
          <input
            id="cap"
            value={cap}
            onChange={(e) => setCap(e.target.value)}
            inputMode="numeric"
            style={{ width: 90 }}
          />
          <button className="btn" type="submit" disabled={busy !== null}>
            {busy === "cap" ? "…" : "Set"}
          </button>
        </form>
        <span className="mut" style={{ fontSize: 12 }}>
          switches from <b>{s.config_source}</b>
          {s.config_source.startsWith("env") &&
            " — the database row is missing or unreadable, so these buttons will not stick"}
        </span>
      </div>
    </Card>
  );
}

/** Stop dialing one person, and the list of everybody already stopped.
 *
 *  A block is not a texting suppression: "stop calling them, keep texting" is an ordinary
 *  thing to want, and routing it through the texting list would quietly drop them off the
 *  drip as well. */
function Blocks({ onDone }: { onDone: () => void }) {
  const [rows, setRows] = useState<RedialBlock[] | null>(null);
  const [phone, setPhone] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = () =>
    api.redialBlocks().then((r) => setRows(r.blocks)).catch((e) => setErr(e.message));
  useEffect(() => {
    load();
  }, []);

  const block = (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim() || !reason.trim()) return;
    setBusy(true);
    setErr(null);
    api
      .redialBlockLead({ phone: phone.trim(), reason: reason.trim() })
      .then(() => {
        setPhone("");
        setReason("");
        return load();
      })
      .then(onDone)
      .catch((e) => setErr(e.message))
      .finally(() => setBusy(false));
  };

  return (
    <Card title="Do not dial">
      <ErrorBanner error={err} />
      <form className="row" style={{ gap: 6, flexWrap: "wrap" }} onSubmit={block}>
        <input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="phone number"
          aria-label="phone to block"
          style={{ width: 150 }}
        />
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="why (they asked us to stop…)"
          aria-label="reason"
          style={{ flex: 1, minWidth: 180 }}
        />
        <button className="btn" type="submit" disabled={busy}>
          {busy ? "…" : "Block"}
        </button>
      </form>
      <span className="mut" style={{ fontSize: 12 }}>
        Stops any cadence running now and refuses new ones. Texting is unaffected.
      </span>
      {rows && rows.length > 0 && (
        <table className="tbl" style={{ marginTop: 10 }}>
          <thead>
            <tr>
              <th>phone</th>
              <th>why</th>
              <th>by</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.phone_last10}>
                <td>{b.phone_last10}</td>
                <td>{b.reason}</td>
                <td className="mut">{b.blocked_by ?? "—"}</td>
                <td>
                  <button
                    className="btn"
                    onClick={() =>
                      api
                        .redialUnblockLead(b.phone_last10)
                        .then(load)
                        .then(onDone)
                        .catch((e) => setErr(e.message))
                    }
                  >
                    Unblock
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {rows && rows.length === 0 && <Empty>Nobody is blocked from dialing.</Empty>}
    </Card>
  );
}

/** Seconds from the reply, as a person would say it. */
function gap(seconds: number) {
  if (seconds === 0) return "now";
  if (seconds < 3600) return `+${Math.round(seconds / 60)}m`;
  const h = seconds / 3600;
  return `+${Number.isInteger(h) ? h : h.toFixed(1)}h`;
}

/** The three switches, as three separate facts.
 *
 *  "The engine is on" and "the engine is dialing" are different things: it can be on and
 *  dry, or on with the dialer off, or on with no credentials. Showing one green light for
 *  all of it would tell somebody people are being called when nobody is. */
function Switches({ s }: { s: RedialStatus }) {
  const dialing = s.engine_enabled && !s.dry_run && s.five9_dialer_enabled;
  return (
    <Card title="State">
      <div style={{ marginBottom: 10 }}>
        {dialing ? (
          <Pill tone="bad">DIALING — real calls are going out</Pill>
        ) : s.engine_enabled && s.dry_run ? (
          <Pill tone="ok">dry run — planning everything, calling nobody</Pill>
        ) : (
          <Pill tone="nu">off</Pill>
        )}
      </div>
      <Row label="engine" value={s.engine_enabled ? "enabled" : "disabled"} />
      <Row label="dry run" value={s.dry_run ? "yes — Five9 is never called" : "no"} />
      <Row
        label="Five9 dialer"
        value={s.five9_dialer_enabled ? "enabled" : "disabled"}
      />
      <Row
        label="credentials"
        value={s.five9_credentials_present ? "present" : "not set"}
        bad={!s.five9_credentials_present}
      />
      <Row label="list" value={s.list_name} />
    </Card>
  );
}

function Row({
  label,
  value,
  bad,
}: {
  label: string;
  value: React.ReactNode;
  bad?: boolean;
}) {
  return (
    <div className="brk" style={{ fontFamily: "var(--sans)", padding: "5px 0" }}>
      <span className="dim">{label}</span>
      <span className="mono" style={{ color: bad ? "var(--bad)" : undefined }}>
        {value}
      </span>
    </div>
  );
}

const counts = (m: Record<string, number>) =>
  Object.entries(m)
    .sort((a, b) => b[1] - a[1])
    .map(([k, v]) => `${fmt.n(v)} ${k.replace(/_/g, " ")}`)
    .join(" · ") || "none";

/** Redialing: the cadence, the rules that stop it, and who is in it.
 *
 *  `removal unverified` is given its own tile rather than being buried in the attempt
 *  counts. It means Five9 did not tell us whether the contact was taken out of the list,
 *  and because Five9's attempt limit is unlimited under this design, somebody in that
 *  state may still be being dialed. It is the one number on this screen that needs a
 *  person. */
export function Redialing() {
  const [s, setS] = useState<RedialStatus | null>(null);
  const [policies, setPolicies] = useState<RedialPolicy[]>([]);
  const [rules, setRules] = useState<RedialRule[]>([]);
  const [tz, setTz] = useState("");
  const [error, setError] = useState<string | null>(null);

  const [phone, setPhone] = useState("");
  const [plans, setPlans] = useState<RedialPlan[] | null>(null);
  const [looking, setLooking] = useState(false);

  // Hoisted out of the effect so the controls can refresh after they change something -
  // without it a button would appear to do nothing until the 30s poll came round.
  const reload = useCallback(
    () =>
      Promise.all([api.redialStatus(), api.redialPolicies(), api.redialRules()])
        .then(([st, po, ru]) => {
          setS(st);
          setPolicies(po.policies);
          setTz(po.window_timezone);
          setRules(ru.rules);
          setError(null);
        })
        .catch((e) => setError(e.message)),
    [],
  );

  useEffect(() => {
    reload();
    const t = setInterval(() => !document.hidden && reload(), 30_000);
    return () => clearInterval(t);
  }, [reload]);

  const lookup = (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim()) return;
    setLooking(true);
    api
      .redialLead({ phone: phone.trim() })
      .then((r) => setPlans(r.plans))
      .catch((err) => setError(err.message))
      .finally(() => setLooking(false));
  };

  return (
    <>
      <h1>Redialing</h1>
      <ErrorBanner error={error} />

      {s && (
        <>
          <div className="tiles">
            {/* Three DIFFERENT states, named so they cannot be read as each other. An `added`
                attempt is in the Five9 list, not called - Five9 dials it when an agent frees
                up, which can be hours. Calling that "dialed" overstated a day 10x. */}
            <Tile
              label="still to be dialled"
              value={fmt.n(s.still_to_be_dialled ?? 0)}
              detail={`${fmt.n(s.people_still_coming ?? 0)} people`}
              tone={(s.still_to_be_dialled ?? 0) > 0 ? "ok" : undefined}
            />
            <Tile
              label="in the Five9 list, not yet called"
              value={fmt.n(s.handed_to_five9_awaiting_dial ?? 0)}
              detail="waiting for a free agent"
            />
            <Tile
              label="CALLS actually made today"
              value={fmt.n(s.dialled_and_confirmed_today ?? 0)}
              detail={`of ${fmt.n(s.handed_to_five9_today ?? 0)} handed to Five9`}
            />
            <Tile label="due now" value={fmt.n(s.due_now)} />
            <Tile
              label="overdue 15m"
              value={fmt.n(s.overdue_15m)}
              tone={s.overdue_15m > 0 ? "ba" : undefined}
            />
            <Tile
              label="removal unverified"
              value={fmt.n(s.removal_unverified)}
              tone={s.removal_unverified > 0 ? "ba" : undefined}
            />
          </div>

          <div className="two">
            <Switches s={s} />
            <Controls s={s} onDone={reload} />
          </div>

          <div className="two">
            <Blocks onDone={reload} />
            <Card title="Queue">
              <Row label="plans" value={counts(s.plans)} />
              <Row label="attempts" value={counts(s.attempts)} />
              {s.removal_unverified > 0 && (
                <p className="dim" style={{ marginTop: 10 }}>
                  {fmt.n(s.removal_unverified)} contact(s) were asked to be removed from{" "}
                  {s.list_name} and Five9 did not confirm it. Five9's attempt limit is
                  unlimited, so they may still be being dialed. Worth checking by hand.
                </p>
              )}
            </Card>
          </div>
        </>
      )}

      <Card title="Cadence">
        {policies.length === 0 ? (
          <Empty>No cadences configured.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>name</th>
                <th>reply day</th>
                <th>after that</th>
                <th>texts</th>
                <th>window ({tz})</th>
              </tr>
            </thead>
            <tbody>
              {policies.map((p) => (
                <tr key={p.key} style={{ opacity: p.enabled ? 1 : 0.5 }}>
                  <td>
                    {p.name}
                    <div className="dim" style={{ fontSize: "0.85em" }}>
                      {p.description}
                    </div>
                  </td>
                  <td className="mono">{p.reply_day_offsets.map(gap).join(" · ")}</td>
                  <td>
                    {p.redial_daily
                      ? `once a day at ${p.redial_at_local.slice(0, 5)}, up to ${p.max_days} days`
                      : "nothing"}
                  </td>
                  <td>{p.send_texts ? "yes" : "no"}</td>
                  <td className="mono">
                    {p.window_open.slice(0, 5)}–{p.window_close.slice(0, 5)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="What a disposition does">
        <p className="dim">
          Highest precedence wins. The tracker outranks Five9 — except Five9's “do not
          call”, which is a legal instruction rather than a sales outcome.
        </p>
        <table>
          <thead>
            <tr>
              <th>precedence</th>
              <th>source</th>
              <th>disposition</th>
              <th>action</th>
              <th>why</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id} style={{ opacity: r.enabled ? 1 : 0.45 }}>
                <td className="mono">{r.precedence}</td>
                <td>{r.source}</td>
                <td className="mono">{r.disposition}</td>
                <td>
                  <Pill
                    tone={
                      r.action.startsWith("stop")
                        ? "bad"
                        : r.action === "continue"
                          ? "nu"
                          : "ok"
                    }
                  >
                    {r.action.replace(/_/g, " ")}
                  </Pill>
                </td>
                <td className="dim">{r.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card title="One person">
        <form onSubmit={lookup} className="brk" style={{ gap: 8 }}>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="phone number"
            aria-label="phone number"
          />
          <button type="submit" disabled={looking}>
            {looking ? "looking…" : "Look up"}
          </button>
        </form>

        {plans !== null &&
          (plans.length === 0 ? (
            <Empty>No redialing plan for that number.</Empty>
          ) : (
            plans.map((p) => (
              <div key={p.id} style={{ marginTop: 14 }}>
                <div className="brk">
                  <strong>
                    {p.entry_type.replace(/_/g, " ")} · {p.status}
                  </strong>
                  <span className="mono dim">
                    {p.dials_done} dial(s) · next{" "}
                    {p.next_action_at ? fmt.when(p.next_action_at) : "—"}
                  </span>
                </div>
                {p.stop_reason && (
                  <p className="dim">stopped: {p.stop_reason}</p>
                )}
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>due</th>
                      <th>status</th>
                      <th>add</th>
                      <th>deleted</th>
                      <th>disposition</th>
                      <th>decided</th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.attempts.map((a) => (
                      <tr key={a.dial_no}>
                        <td className="mono">{a.dial_no}</td>
                        <td className="mono">
                          {fmt.when(a.scheduled_for)}
                          {a.rolled && (
                            <span className="dim" title="moved to stay inside the dialing window">
                              {" "}
                              ↷
                            </span>
                          )}
                        </td>
                        <td>
                          <Pill
                            tone={
                              a.status === "removal_unverified"
                                ? "bad"
                                : a.status === "removed"
                                  ? "ok"
                                  : "nu"
                            }
                          >
                            {a.status.replace(/_/g, " ")}
                          </Pill>
                        </td>
                        {/* 603 means the record was already there, so no call was queued. */}
                        <td className="mono" title={a.add_code === 603 ? "already in list — no dial queued" : ""}>
                          {a.add_code ?? "—"}
                        </td>
                        <td className="mono">{a.records_deleted ?? "—"}</td>
                        <td>
                          {a.tracker_disposition || a.five9_disposition || "—"}
                        </td>
                        <td className="dim" title={a.decided_reason ?? ""}>
                          {a.decided_action ?? "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {p.attempts.some((a) => a.last_error) && (
                  <p className="dim">
                    {p.attempts.filter((a) => a.last_error).slice(-1)[0]?.last_error}
                  </p>
                )}
              </div>
            ))
          ))}
      </Card>
    </>
  );
}
